import {
  AudioEngineV2,
  Color3,
  CreateAudioEngineAsync,
  DynamicTexture,
  Engine,
  FreeCamera,
  Gamepad,
  GamepadManager,
  HavokPlugin,
  LinesMesh,
  Mesh,
  MeshBuilder,
  Observable,
  Observer,
  ParticleHelper,
  ParticleSystemSet,
  Path3D,
  PhysicsViewer,
  Scene,
  StandardMaterial,
  Vector3,
} from '@babylonjs/core';

import { HavokPhysicsWithBindings } from '@babylonjs/havok';
import { ShowInspector } from '@babylonjs/inspector';
import { AssetFactory } from './AssetFactory';
import { GameConfig } from './GameConfig';
import { GameFinish } from './GameFinish';
import { GameOver } from './GameOver';
import { GameSettings } from './GameSettings';
import { GameState } from './GameState';
import { Gui2D } from './gui/Gui2D';
import { Gui3D } from './gui/Gui3D';
import { Intro } from './Intro';
import { Level, LevelResult } from './Level';
import { LevelConfigLoader } from './LevelConfigLoader';
import { LevelSelection } from './LevelSelection';
import { LevelSuccess } from './LevelSuccess';
import { LoadingScreen } from './LoadingScreen';
import { MainMenu } from './MainMenu';
import { MaterialService } from './MaterialService';
import { ShaderFactory } from './ShaderFactory';
import { SoundService } from './SoundService';
import { LevelTemplateFactory, LevelTemplateKey } from './templates/LevelTemplate';
import { User } from './User';

export class Game {
  focusCanvas: Observable<void> = new Observable();

  engine: Engine;
  physicsPlugin!: HavokPlugin;
  audioEngine!: AudioEngineV2;
  scene!: Scene;
  physicsViewer!: PhysicsViewer;
  guiScene!: Scene;
  guiCamera!: FreeCamera;

  gamepadManager!: GamepadManager;

  shaders!: ShaderFactory;

  // Game components
  level!: Level;
  materialService!: MaterialService;
  sound!: SoundService;

  assetFactory: AssetFactory;
  loadingScreen: LoadingScreen;

  levelKey?: string;
  gameState: GameState;
  templateKey?: string;

  gui2D: Gui2D;
  gui3D: Gui3D;

  mobile: boolean = false;
  paused: boolean = false;

  user: User;

  /**
   * Observers that can be removed in routeTo
   */
  removeableObservers: Observer<any>[] = [];

  constructor(
    public canvas: HTMLCanvasElement,
    public havoc: HavokPhysicsWithBindings,
    public config: GameConfig
  ) {
    ParticleHelper.BaseAssetsUrl = 'assets/particles/predefined';
    ParticleSystemSet.BaseAssetsUrl = ParticleHelper.BaseAssetsUrl;

    // load 3D engine
    this.engine = new Engine(canvas, true, undefined, false);
    this.engine.enableOfflineSupport = false;

    this.loadingScreen = new LoadingScreen(this.focusCanvas, 'Engine loaded...');
    // replace the default loading screen
    this.engine.loadingScreen = this.loadingScreen;

    // show the loading screen
    this.loadingScreen.displayLoadingUI();

    // Initialize and start the game
    this.initGame();

    // the canvas/window resize event handler
    window.addEventListener('resize', () => {
      this.engine.resize();
    });

    this.focusCanvas.add(() => {
      this.canvas.focus();
    });

    // Pause game when user switches to another tab
    document.addEventListener('visibilitychange', () => {
      if (this.gameState === GameState.LEVEL && this.level) {
        if ((document.hidden && !this.paused) || (!document.hidden && this.paused)) {
          this.pause();
        }
      }
    });
  }

  static fireMessage(message: string): void {
    // Fire EditorMessage event to parent window (editor)
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'EditorMessage', message }, '*');
    } else {
      // Fallback to console if not in iframe
      console.log(message);
    }
  }

  async initGame() {
    try {
      this.user = await User.load();

      this.levelKey = this.config.levelKey;
      this.gameState = this.config.state;

      const urlParams = new URLSearchParams(window.location.search);
      const levelKeyParam = urlParams.get('levelKey');
      const gameStateParam = urlParams.get('gameState');
      const debugParam = urlParams.get('debug');
      const sceneViewerParam = urlParams.get('sceneViewer');
      const muteParam = urlParams.get('mute');
      const templateParam = urlParams.get('template');

      if (levelKeyParam) {
        this.levelKey = levelKeyParam;
      }
      if (gameStateParam) {
        this.gameState = parseInt(gameStateParam);
      }
      if (debugParam) {
        this.config.debug = debugParam === 'true';
      }

      if (muteParam) {
        this.config.mute = muteParam === 'true';
      }

      if (templateParam) {
        this.config.levelTemplate = templateParam as LevelTemplateKey;
      }

      if (sceneViewerParam) {
        this.config.sceneViewer = sceneViewerParam === 'true';
      }

      this.gamepadManager = new GamepadManager();
      this.audioEngine = await CreateAudioEngineAsync();
      this.audioEngine.unlockAsync();
      this.sound = new SoundService(this);
      this.shaders = new ShaderFactory(this);

      this.loadAudio();

      this.initScene();

      // Focus canvas for input
      this.canvas.focus();

      // Route creates the level/menu with camera and starts render loop
      await this.route();
    } catch (error) {
      console.error('[Game] Error during initialization:', error);
      // Try to hide loading screen even on error so user can see console
      this.loadingScreen.hideLoadingUI();
    }
  }

  async loadAudio() {
    await this.sound.create('click', 'click', 'fx');
    await this.sound.create('finish', 'bonus', 'fx');
    await this.sound.create('level-start', 'success3', 'fx');
    await this.sound.create('goal', 'bonus', 'fx');

    await this.sound.create('enemywalk', 'walk', 'fx', { playbackRate: 1.25, pitch: 2 });
    await this.sound.create('enemyjump', 'jump', 'fx');
    await this.sound.create('enemyprojectilehit', 'hit', 'fx');
    await this.sound.create('enemydestroy', 'cartoon-splat', 'fx');

    await this.sound.create('obstaclemotor', 'motor-loop', 'fx');
    await this.sound.create('obstacleengine', 'engine3', 'fx');
    await this.sound.create('obstaclehydraulic', 'hydraulic_up', 'fx');

    await this.sound.create('clock-ticking', 'clock-ticking', 'fx');
    await this.sound.create('kick', 'kick', 'fx');
    await this.sound.create('slap', 'slap', 'fx');
    await this.sound.create('swipe', 'swipe', 'fx');
    await this.sound.create('jump', 'jump', 'fx');
    await this.sound.create('airjump', 'jump6', 'fx');
    await this.sound.create('damage', 'damage', 'fx');
    await this.sound.create('hit', 'hit', 'fx');
    await this.sound.create('laser', 'laser', 'fx');
    await this.sound.create('lifelost', 'life-lost', 'fx', { volume: 0.4 });
    await this.sound.create('miss', 'boom', 'fx');
    await this.sound.create('swoosh', 'swoosh', 'fx');
    await this.sound.create('pickup', 'pickup', 'fx');
    await this.sound.create('pop', 'pop_3', 'fx');
    await this.sound.create('collect', 'powerUp2', 'fx');
    await this.sound.create('collectCoin', 'coins_gather_small', 'fx');
    await this.sound.create('collectHeart', 'heart_collect', 'fx');
    await this.sound.create('collectGem', 'gem_collect', 'fx');
    await this.sound.create('run', 'foley_footstep_concrete_1', 'fx', { playbackRate: 1, volume: 0.05 });
    await this.sound.create('walkback', 'foley_footstep_concrete_1', 'fx', { playbackRate: 0.45, volume: 0.05 });
    await this.sound.create('shieldoff', 'pop_3', 'fx');
    await this.sound.create('checkpoint', 'successbeep', 'fx');
    await this.sound.create('projectilehit', 'hit', 'fx');
  }

  gameLoop(deltaTime: number) {
    if (this.level) {
      this.level.update(deltaTime);
    }
  }

  getNextLevel(key: string): { nextKey: string; nextIndex: number } {
    if (key === 'test') {
      return { nextKey: 'test', nextIndex: -1 };
    }

    const index = LevelConfigLoader.getLevelIndex(key);
    const nextIndex = index + 1;
    const nextKey = LevelConfigLoader.getLevelKey(nextIndex);

    return { nextKey, nextIndex };
  }

  nextLevel(key: string) {
    const { nextKey, nextIndex } = this.getNextLevel(key);
    this.config.levelKey = nextKey;
    this.routeTo(GameState.LEVEL);
  }

  async loadLevel(key: string): Promise<Level> {
    if (key === 'test') {
      return new Level(this, key, LevelTemplateFactory.get(this.config.levelTemplate).create());
    }
    if (GameConfig.logDebug) {
      console.log('[Game] Loading level: ', key);
    }
    const config = await LevelConfigLoader.load(key);
    return new Level(this, key, config);
  }

  render(updateCameras?: boolean, ignoreAnimations?: boolean) {
    this.scene.render(updateCameras, ignoreAnimations);
    this.guiScene.render(updateCameras, ignoreAnimations);
  }

  pause() {
    if (this.paused) {
      this.paused = false;
      this.scene.physicsEnabled = true;
      this.sound.pauseBackgroundMusic();
      this.engine.runRenderLoop(this.render.bind(this));
    } else {
      this.paused = true;
      this.level.hud.showMessage('Paused', 100, true);
      setTimeout(() => {
        this.sound.pauseBackgroundMusic();
        this.scene.physicsEnabled = false;
        this.engine.stopRenderLoop();
      }, 200);
    }
  }

  routeTo(gameState: GameState, params?: any, displayLoading: boolean = true) {
    // Stop render loop during transition to prevent "No camera" errors
    this.engine.stopRenderLoop();

    this.sound.stopBackgroundMusic();

    this.removeableObservers.forEach((observer) => {
      observer.remove();
    });
    this.removeableObservers = [];

    if (displayLoading) {
      this.engine.displayLoadingUI();
    }
    this.gameState = gameState;

    if (this.level) {
      this.level.dispose();
    }
    this.level = undefined;
    this.initScene();
    this.route(params);
  }

  async route(params?: any) {
    if (this.gameState === GameState.MAIN) {
      await this.createMainMenu(params);
    } else if (this.gameState === GameState.SELECTION) {
      await this.createLevelSelection(params);
    } else if (this.gameState === GameState.OVER) {
      await this.createGameOver(params);
    } else if (this.gameState === GameState.LEVEL) {
      await this.createLevel(params);
    } else if (this.gameState === GameState.SUCCESS) {
      await this.createLevelSuccess(params);
    } else if (this.gameState === GameState.SETTINGS) {
      await this.createSettings(params);
    } else if (this.gameState === GameState.FINISH) {
      await this.createFinish(params);
    } else if (this.gameState === GameState.INTRO) {
      await this.createIntro(params);
    }
    // Restart render loop after route completes
    this.engine.runRenderLoop(this.render.bind(this));
  }

  async createIntro(params?: any) {
    await new Intro(this).create();
    this.loadingScreen.hideLoadingUI();
  }

  async createMainMenu(params?: any) {
    if (!this.user.intro) {
      await this.createIntro();
      return;
    }
    await new MainMenu(this).create();
    this.loadingScreen.hideLoadingUI();
  }

  async createFinish(params?: any) {
    await new GameFinish(this).create();
    this.loadingScreen.hideLoadingUI();
  }

  async createGameOver(params?: any) {
    await new GameOver(this).create();
    this.loadingScreen.hideLoadingUI();
  }

  async createLevelSelection(params?: any) {
    await new LevelSelection(this).create();
    this.loadingScreen.hideLoadingUI();
  }

  async createSettings(params?: any) {
    await new GameSettings(this).create();
    this.loadingScreen.hideLoadingUI();
  }

  async createLevelSuccess(params?: any) {
    let levelResult: LevelResult = params;
    await new LevelSuccess(this, levelResult).create();
    this.loadingScreen.hideLoadingUI();
  }

  async createLevel(params?: any) {
    try {
      this.level = await this.loadLevel(this.levelKey);
      await this.level.build();
      this.loadingScreen.hideLoadingUI(10);
    } catch (error) {
      console.error('[Game] Error creating level', this.levelKey, ':', error);
      this.loadingScreen.hideLoadingUI();
      throw error; // Re-throw to prevent render loop from starting
    }
  }

  initScene() {
    if (this.scene) {
      this.scene.dispose();
    }

    if (this.guiScene) {
      this.guiScene.dispose();
    }

    if (this.physicsPlugin) {
      this.physicsPlugin.dispose();
    }

    if (this.physicsViewer) {
      this.physicsViewer.dispose();
    }

    this.guiScene = new Scene(this.engine);
    this.guiScene.autoClear = false;

    this.guiCamera = new FreeCamera('guiCamera', new Vector3(0, 0, 0), this.guiScene);
    this.guiCamera.mode = FreeCamera.ORTHOGRAPHIC_CAMERA;

    this.scene = new Scene(this.engine);
    this.scene.autoClear = true;
    this.scene.collisionsEnabled = true;

    // Register game loop
    this.scene.registerBeforeRender(() => {
      if (!this.paused) {
        let deltaTime = this.engine.getDeltaTime() / 1000;
        // Cap deltaTime to prevent physics explosions after loading/pausing
        if (deltaTime > 0.1) {
          deltaTime = 0.1;
        }
        this.gameLoop(deltaTime);
      }
    });

    this.gui3D = new Gui3D(this);
    this.gui2D = new Gui2D(this);

    this.materialService = new MaterialService(this);

    this.assetFactory = new AssetFactory(this);

    this.createPhysics();

    // debugging
    if (this.config.debug) {
      if (this.config.debugLayer) {
        ShowInspector(this.scene, {
          leftPaneDefaultCollapsed: true,
        });
      }
    }
  }

  createPhysics() {
    this.physicsPlugin = new HavokPlugin(true, this.havoc);
    this.scene.enablePhysics(Level.fullGravity, this.physicsPlugin);
    this.physicsViewer = new PhysicsViewer(this.scene);
  }

  gamepad() {
    return this.gamepadManager.gamepads[0];
  }

  setupGamepad(callback: (gamepad: Gamepad) => void) {
    if (this.gamepad()) {
      callback(this.gamepad());
    } else {
      this.gamepadManager.onGamepadConnectedObservable.add((gamepad) => {
        callback(gamepad);
      });
    }
  }

  debugPath3D(path3d: Path3D) {
    const size = 0.5;
    const step = 1;
    const curve = path3d.getCurve();
    const tgts = path3d.getTangents();
    const norms = path3d.getNormals();
    const binorms = path3d.getBinormals();
    let vcTgt: LinesMesh, vcNorm: LinesMesh, vcBinorm: LinesMesh;
    const line = MeshBuilder.CreateLines('curve', { points: curve });
    for (let i = 0; i < curve.length; i += step) {
      vcTgt = MeshBuilder.CreateLines('tgt' + i, {
        points: [curve[i], curve[i].add(tgts[i].scale(size))],
      });
      vcNorm = MeshBuilder.CreateLines('norm' + i, {
        points: [curve[i], curve[i].add(norms[i].scale(size))],
      });
      vcBinorm = MeshBuilder.CreateLines('binorm' + i, {
        points: [curve[i], curve[i].add(binorms[i].scale(size))],
      });
      vcTgt.color = Color3.Red();
      vcNorm.color = Color3.Green();
      vcBinorm.color = Color3.Blue();
    }
  }

  createTextPlane(text: string, color: string, size: number) {
    const dynamicTexture = new DynamicTexture('DynamicTexture', 50, this.scene, true);
    dynamicTexture.hasAlpha = true;
    dynamicTexture.drawText(text, 5, 40, 'bold 36px Arial', color, 'transparent', true);
    const plane = Mesh.CreatePlane('TextPlane', size, this.scene, true);
    const mat = new StandardMaterial('TextPlaneMaterial', this.scene);
    mat.backFaceCulling = false;
    mat.specularColor = new Color3(0, 0, 0);
    mat.diffuseTexture = dynamicTexture;

    plane.material = mat;
    return plane;
  }

  debugWorldAxis(size: number) {
    const axisX = Mesh.CreateLines(
      'axisX',
      [
        Vector3.Zero(),
        new Vector3(size, 0, 0),
        new Vector3(size * 0.95, 0.05 * size, 0),
        new Vector3(size, 0, 0),
        new Vector3(size * 0.95, -0.05 * size, 0),
      ],
      this.scene,
      false
    );
    axisX.color = new Color3(1, 0, 0);
    const xChar = this.createTextPlane('X', 'red', size / 10);
    xChar.position = new Vector3(0.9 * size, -0.05 * size, 0);
    const axisY = Mesh.CreateLines(
      'axisY',
      [
        Vector3.Zero(),
        new Vector3(0, size, 0),
        new Vector3(-0.05 * size, size * 0.95, 0),
        new Vector3(0, size, 0),
        new Vector3(0.05 * size, size * 0.95, 0),
      ],
      this.scene,
      false
    );
    axisY.color = new Color3(0, 1, 0);
    const yChar = this.createTextPlane('Y', 'green', size / 10);
    yChar.position = new Vector3(0, 0.9 * size, -0.05 * size);
    const axisZ = Mesh.CreateLines(
      'axisZ',
      [
        Vector3.Zero(),
        new Vector3(0, 0, size),
        new Vector3(0, -0.05 * size, size * 0.95),
        new Vector3(0, 0, size),
        new Vector3(0, 0.05 * size, size * 0.95),
      ],
      this.scene,
      false
    );
    axisZ.color = new Color3(0, 0, 1);
    const zChar = this.createTextPlane('Z', 'blue', size / 10);
    zChar.position = new Vector3(0, 0.05 * size, 0.9 * size);
  }
}
